import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { PANEL_ROLES } from '../lib/homeRoute';
import { puedeSolicitar, puedeVer, soloSupervisaTiendas, type Pantalla } from '../domain/permissions';
import MarcaTreidOps from './MarcaTreidOps';
import { useAuth } from './AuthProvider';
import PendingBadge from './PendingBadge';
import AutorizacionesBadge from './AutorizacionesBadge';

/**
 * `pantalla`: clave en la matriz de `domain/permissions.ts`, que es la que decide quién ve
 * qué. Antes cada link llevaba su propia lista de `roles` y la mitad no llevaba ninguna, así
 * que el coordinador y el supervisor veían "Cierres" y "Ajustes" —dos pantallas que solo les
 * muestran un muro— y no veían nada que no vieran los demás. La matriz es ahora el único
 * lugar donde eso se cambia, y la comparten el menú y los guardas de ruta del router.
 *
 * `badge`: contador que se pinta junto a la etiqueta (Task 37).
 */
export const PANEL_LINKS: {
  to: string;
  label: string;
  pantalla: Pantalla;
  icono?: string;
  badge?: ReactNode;
}[] = [
  // El resumen gerencial, dentro del panel (0396): admin y dirección. Dirección entra directo acá.
  { to: '/panel/resumen', label: 'Resumen', pantalla: 'resumen' },
  { to: '/panel/hoy', label: 'Hoy', pantalla: 'hoy' },
  { to: '/panel/planificacion', label: 'Planificación', pantalla: 'planificacion' },
  { to: '/panel/campanias', label: 'Campañas', pantalla: 'campanias' },
  { to: '/panel/importar-programacion', label: 'Importar programación', pantalla: 'importar' },
  { to: '/panel/asignaciones', label: 'Asignaciones', pantalla: 'asignaciones' },
  { to: '/panel/aprobaciones', label: 'Aprobaciones', pantalla: 'aprobaciones' },
  { to: '/panel/trabajadores', label: 'Trabajadores', pantalla: 'trabajadores' },
  { to: '/panel/documentos', label: 'Documentos', pantalla: 'documentos' },
  {
    to: '/panel/autorizaciones',
    label: 'Autorizaciones',
    pantalla: 'autorizaciones',
    badge: <AutorizacionesBadge />,
  },
  { to: '/panel/pdv', label: 'PDV', pantalla: 'pdv' },
  { to: '/panel/validacion', label: 'Validación', pantalla: 'validacion' },
  /**
   * «Canjes» paso a llamarse «Registros de campo» el 2026-09-05: adentro ya no hay solo
   * canjes —tambien caen los cierres de turno de la dinamica de impulso— y el nombre viejo
   * mandaba a buscar el formulario donde no estaba.
   *
   * La RUTA sigue siendo `/panel/canjes` y la clave de permiso sigue siendo `canjes`: es un
   * cambio de rotulo, no de estructura. Cambiar la URL romperia los enlaces guardados y no
   * mejoraria nada que el usuario vea.
   */
  { to: '/panel/canjes', label: 'Registros de campo', pantalla: 'canjes', icono: '⭐' },
  /**
   * «Levantamiento» SE QUITÓ del menú el 2026-09-05, por pedido del usuario: *«no lo borres, solo
   * ocúltalo, sácalo de la vista… no lo voy a usar por ahora, y si después lo pienso reciclar te
   * aviso»*. Es la misma decisión que ya se tomó con el levantamiento de canal en el celular.
   *
   * **La pantalla, la ruta `/panel/levantamiento` y su permiso SIGUEN EXISTIENDO.** Quitarle el
   * acceso en `domain/permissions.ts` habría sido otra cosa: el guarda de ruta lo comparte con el
   * menú, así que poner la pantalla en `nada` la volvería inalcanzable también por URL, y con ella
   * las fichas de Softys que ya están registradas. Acá solo se va la entrada del menú.
   */
  /**
   * «Levantamiento P&G» pasó a llamarse «Levantamiento inf.» (de información) el 2026-09-30: adentro
   * ya no está solo P&G, entró Yichang (Pringles y Kellogg's), y el cliente se elige en la pantalla.
   *
   * La RUTA sigue siendo `/panel/levantamiento-pg` y la clave de permiso sigue siendo
   * `levantamientoPg`, por el mismo criterio que «Registros de campo» mantiene `/panel/canjes`: es un
   * cambio de rótulo, no de estructura.
   *
   * ⚠️ El rótulo NO puede ser «Levantamiento» a secas: esa es la entrada del estudio de SOFTYS,
   * oculta del menú desde el 5-set pero con la ruta viva. Llamarlas igual reproduciría la confusión
   * que el 15-set hizo que un agente leyera el estudio equivocado y contestara con total seguridad.
   */
  { to: '/panel/levantamiento-pg', label: 'Levantamiento inf.', pantalla: 'levantamientoPg' },
  { to: '/panel/academia', label: 'Academia', pantalla: 'academia' },
  { to: '/panel/cierres', label: 'Cierres', pantalla: 'cierres' },
  { to: '/panel/ajustes', label: 'Ajustes', pantalla: 'ajustes' },
];

/** `aria`: nombre accesible cuando la etiqueta visible tiene que ser más corta que el
 *  concepto (una pestaña de 93 px en un celular no aguanta "Cambiar mi clave"). */
export const MOVIL_LINKS: { to: string; label: string; end: boolean; aria?: string }[] = [
  { to: '/movil', label: 'Mi turno', end: true },
  { to: '/movil/mi-mes', label: 'Mi mes', end: false },
  /**
   * «Puestos» (levantamiento de canal) SE QUITÓ del menú el 2026-09-04, por decisión del usuario:
   * *«fue un momento que lo necesitamos hacer, por eso lo incluimos ahí, pero no iba a ser parte
   * del plan mayor […] no va a ir una pestaña llamada Puestos»*.
   *
   * La pantalla y su ruta (`/movil/ficha-puesto`) SIGUEN EXISTIENDO y no se borran: el usuario dijo
   * que la estructura se va a aprovechar si se repite el trabajo. Lo que se va es la pestaña, que
   * la veían los 47 trabajadores de campo tuvieran o no campaña de levantamiento — y desde mañana
   * la verían las 11 impulsadoras de Bimbo, a las que no les corresponde.
   *
   * Sigue alcanzable por su URL y desde el cierre de jornada, que es de donde la usa quien la
   * necesita de verdad.
   */
  { to: '/movil/historial', label: 'Historial', end: false },
  // Ruta EXACTA `/movil/solicitud` (la Task 27 crea la página y la Task 37 la reutiliza).
  { to: '/movil/solicitud', label: 'Solicitudes', end: false },
  // Acceso permanente al cambio de clave: el primero es forzado, los demás salen de aquí.
  { to: '/cambiar-clave', label: 'Mi clave', end: false, aria: 'Cambiar mi clave' },
];

/**
 * «Mi ruta» — la pestaña del SUPERVISOR DE RUTA (0092), la única del menú de campo condicionada.
 *
 * No la habilita el ROL, y esa es la corrección del 2026-09-05. Al principio se gateó en
 * `coordinador`, y con eso la veía también David, que coordina desde la oficina y no recorre
 * tiendas. El usuario lo cortó: *«esto sería una sección específicamente para los supervisores…
 * los coordinadores tampoco, porque no les compete»*.
 *
 * La habilita `profiles.hace_ruta` (0096), una bandera por persona. No se creó un rol
 * «supervisor» porque habría obligado a tocar el CHECK de `profiles`, la matriz de permisos, los
 * guardas de ruta y dos listas escritas a mano, y a reasignarle el rol a gente que hoy trabaja
 * bien. La bandera se enciende y se apaga por persona, que es la granularidad del problema.
 */
const MOVIL_LINK_RUTA = { to: '/movil/mi-ruta', label: 'Mi ruta', end: false };

/**
 * El menú de campo de ESTA persona.
 *
 * Hasta la 0092 el menú era una constante: la misma lista para todos. Se volvió función porque hay
 * una sola pestaña que no es de todos, y la alternativa —esconderla con un `&&` dentro del `map`—
 * dejaría el menú sin una lista que se pueda afirmar en una prueba, que es justamente lo que evitó
 * que «Puestos» volviera a colarse.
 */
export function movilLinks(haceRuta: boolean | undefined): typeof MOVIL_LINKS {
  return haceRuta === true ? [...MOVIL_LINKS, MOVIL_LINK_RUTA] : MOVIL_LINKS;
}

/** Un enlace que no es del router: `/cotizaciones/` es otra entrada de Vite y se abre con recarga completa. */
export interface EnlaceExterno {
  to: string;
  label: string;
  externo: true;
}

/**
 * Los enlaces del menú con «Cotizaciones Treid» antes de «Campañas» si la persona puede pedir compras: comprador o
 * solicitante (spec §2.2, condición 6). Si no ve «Campañas», va al final. Un NavLink no sirve: el comodín del router lo
 * devolvería a `/`.
 */
export function enlacesDelMenu<T extends { to: string }>(visibles: T[], veCotizaciones: boolean): (T | EnlaceExterno)[] {
  if (!veCotizaciones) return visibles;
  const cotizaciones: EnlaceExterno = { to: '/cotizaciones/', label: 'Cotizaciones Treid', externo: true };
  const i = visibles.findIndex((l) => l.to === '/panel/campanias');
  return i < 0 ? [...visibles, cotizaciones] : [...visibles.slice(0, i), cotizaciones, ...visibles.slice(i)];
}

function useLogout() {
  const navigate = useNavigate();
  return async () => {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };
}

export function PanelLayout() {
  const { profile, roles, haceRuta } = useAuth();
  const logout = useLogout();
  // En < lg el menú lateral se despliega sobre el contenido; en >= lg está siempre fijo.
  const [menuAbierto, setMenuAbierto] = useState(false);
  const cerrarMenu = () => setMenuAbierto(false);
  const visibleLinks = enlacesDelMenu(PANEL_LINKS.filter((l) => puedeVer(l.pantalla, roles)), puedeSolicitar(roles));
  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Barra superior solo en pantallas chicas (en >= lg no existe). */}
      <header className="fixed inset-x-0 top-0 z-20 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => setMenuAbierto(true)}
          aria-label="Abrir menú"
          aria-expanded={menuAbierto}
          className="rounded p-1 text-gray-700 hover:bg-gray-100"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <MarcaTreidOps tamano="sm" />
      </header>
      {menuAbierto && (
        <div
          onClick={cerrarMenu}
          aria-hidden="true"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}
      {/*
        El menú se queda QUIETO al desplazar la página.

        Estaba como `lg:static`: en escritorio entraba en el flujo normal, así que crecía con el
        alto de la página y "Cambiar mi clave" y "Cerrar sesión" —que van al final— terminaban a
        miles de píxeles abajo. Con una tabla larga a la derecha (Validación, Asignaciones) había
        que desplazarse hasta el último registro para poder cerrar sesión.

        `lg:sticky lg:top-0 lg:h-screen` lo ancla al alto de la ventana y lo pega arriba: la barra
        deja de depender de cuánto mida el contenido de al lado. En celular ya estaba bien, porque
        ahí es `fixed inset-y-0`.

        El que desplaza es el `<nav>`, no la barra: si algún día hay más secciones de las que
        caben, se desplaza la lista de enlaces y el pie con la cuenta sigue a la vista. Es lo que
        hace que esto no se vuelva a romper al agregar secciones.
      */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-56 flex-col border-r border-gray-200 bg-white transition-transform lg:sticky lg:top-0 lg:h-screen lg:z-auto lg:translate-x-0 lg:transition-none ${
          menuAbierto ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="px-4 py-5">
          <MarcaTreidOps tamano="md" />
          <p className="mt-2 truncate text-xs text-gray-500">
            {profile ? `${profile.first_names} ${profile.last_names}` : ''}
          </p>
        </div>
        {/* Puente al modo de campo: quien también es recurso marca su propia asistencia. El supervisor
            puro no marca (va por «Mi ruta», abajo): este botón lo mandaba a un «Mi turno» vacío. */}
        {roles.includes('recurso') && !soloSupervisaTiendas(roles) && (
          <Link
            to="/movil"
            onClick={cerrarMenu}
            className="mx-2 mb-3 block rounded bg-blue-600 px-3 py-2 text-center text-sm font-medium text-white hover:bg-blue-700"
          >
            Marcar asistencia
          </Link>
        )}
        {/*
          Segundo puente, para el supervisor (0092). Es OTRO botón y no el de arriba con la
          condición ampliada, por dos motivos: dice lo que la persona va a hacer —el supervisor NO
          marca asistencia, así que mandarlo por un botón que dice «Marcar asistencia» sería
          mentirle— y lo deja en su pantalla, no en la de turnos.

          Sin esto, quien recorre ruta y no tiene rol `recurso` —hoy Mariana López, que supervisa
          10 tiendas de Bimbo— no tendría ningún enlace visible hacia el celular: tendría que
          escribir la URL a mano.

          Se rige por la MISMA bandera que la pestaña (`hace_ruta`), no por el rol: si el botón y la
          pestaña se decidieran distinto, un día habría uno sin el otro.
        */}
        {haceRuta === true && (
          <Link
            to="/movil/mi-ruta"
            onClick={cerrarMenu}
            className="mx-2 mb-3 block rounded border border-blue-300 bg-blue-50 px-3 py-2 text-center text-sm font-medium text-blue-800 hover:bg-blue-100"
          >
            Mi ruta
          </Link>
        )}
        <nav className="flex-1 space-y-1 overflow-y-auto px-2">
          {visibleLinks.map((l) =>
            'externo' in l ? (
              <a
                key={l.to}
                href={l.to}
                className="block rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={cerrarMenu}
                className={({ isActive }) =>
                  `block rounded px-3 py-2 text-sm ${
                    isActive ? 'bg-blue-600 font-medium text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                {/* Decorativo: `aria-hidden` para que el lector de pantalla lea la seccion y no
                    «estrella Registros de campo». */}
                {l.icono && (
                  <span aria-hidden="true" className="mr-1.5">
                    {l.icono}
                  </span>
                )}
                {l.label}
                {l.badge}
                {l.to === '/panel/aprobaciones' && <PendingBadge />}
              </NavLink>
            ),
          )}
        </nav>
        {/* Cuenta, no sección: va abajo con el cierre de sesión y no dentro de la navegación. */}
        <Link
          to="/cambiar-clave"
          onClick={cerrarMenu}
          className="mx-2 mt-2 rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
        >
          Cambiar mi clave
        </Link>
        <button
          onClick={logout}
          className="m-2 rounded px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-100"
        >
          Cerrar sesión
        </button>
      </aside>
      <main className="max-lg:min-w-0 flex-1 overflow-x-auto p-4 pt-20 lg:p-6 lg:pt-6">
        <Outlet />
      </main>
    </div>
  );
}

export function MovilLayout() {
  const { profile, roles, haceRuta } = useAuth();
  const logout = useLogout();
  const esDePanel = roles.some((r) => PANEL_ROLES.includes(r));
  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="flex items-center justify-between gap-2 bg-blue-600 px-4 py-3 text-white">
        <div className="min-w-0">
          <MarcaTreidOps tamano="sm" tono="claro" />
          <p className="mt-1 truncate text-xs opacity-90">
            {profile ? `${profile.first_names} ${profile.last_names}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Puente de vuelta al panel para quien además tiene un rol de oficina. */}
          {esDePanel && (
            <Link
              to="/panel/hoy"
              className="rounded bg-white px-3 py-1 text-xs font-semibold text-blue-700"
            >
              Ir al panel
            </Link>
          )}
          <button onClick={logout} className="rounded bg-blue-700 px-3 py-1 text-xs">
            Salir
          </button>
        </div>
      </header>
      {/* El menú va ARRIBA y pegado, no abajo. Pedido del usuario, y además resuelve dos cosas:
          en la parte inferior competía con la barra del navegador del celular, y —lo importante—
          la pantalla de registrar un canje no lo mostraba, así que desde ahí no había forma de
          volver salvo el botón de cancelar del final del formulario. Arriba aparece en TODAS las
          pantallas de campo por igual, que es lo que se pidió. */}
      {/* `overflow-x-auto` + `whitespace-nowrap`: con cinco pestañas entran todas y `flex-1` las
          reparte; con seis —la del supervisor— la más larga («Solicitudes») ya no cabe en 375 px y
          la barra se desplaza en vez de partir la palabra en dos líneas. */}
      <nav className="sticky top-0 z-10 flex overflow-x-auto border-b border-gray-200 bg-white shadow-sm">
        {movilLinks(haceRuta).map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            aria-label={l.aria}
            className={({ isActive }) =>
              `flex-1 whitespace-nowrap px-2 py-3 text-center text-sm ${
                isActive ? 'font-bold text-blue-600' : 'text-gray-600'
              }`
            }
          >
            {l.label}
          </NavLink>
        ))}
      </nav>
      <main className="flex-1 p-4">
        <Outlet />
      </main>
    </div>
  );
}
